import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Language, DEFAULT_LANGUAGE, isValidLanguage } from './config';
import { getLocalizedSlug, getCanonicalSlug } from './slugs';
import { supabase } from '../supabase';
import { useSessionStore } from '../../store/sessionStore';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translateUrl: (url: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<Language>(
    () => {
      // First check URL path
      const pathParts = location.pathname.split('/');
      const urlLang = pathParts[1];
      if (isValidLanguage(urlLang)) {
        return urlLang;
      }

      // Then check browser language
      const browserLang = navigator.language.split('-')[0].toLowerCase();
      console.log('Browser language detected:', browserLang);
      if (isValidLanguage(browserLang)) {
        return browserLang;
      }

      // Finally fallback to default
      return DEFAULT_LANGUAGE;
    }
  );

  // Get current session from the store
  const session = useSessionStore((state) => state.session);

  // Initialize i18n with detected language
  useEffect(() => {
    console.log('Setting initial language to:', language);
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
  }, []);

  // Handle language changes from URL
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const urlLang = pathParts[1];

    if (isValidLanguage(urlLang) && urlLang !== language) {
      setLanguageState(urlLang);
      i18n.changeLanguage(urlLang);
      document.documentElement.lang = urlLang;
    }
  }, [location.pathname, language, i18n]);

  // Update meta tags when language changes
  useEffect(() => {
    // Update OG image based on language
    const metaTags = document.querySelectorAll('meta[property^="og:image"], meta[name="twitter:image"]');
    
    metaTags.forEach(tag => {
      const currentContent = tag.getAttribute('content') || '';
      if (currentContent.includes('/og-image-')) {
        const newContent = currentContent.replace(/\/og-image-[a-z]+\.(svg|png)/, `/og-image-${language}.$1`);
        tag.setAttribute('content', newContent);
      }
    });
  }, [language]);

  // Update session language in database when language changes and we have an active session
  useEffect(() => {
    const updateSessionLanguage = async () => {
      if (session?.id) {
        try {
          const { error } = await supabase
            .from('sessions')
            .update({ language })
            .eq('id', session.id);
          
          if (error) {
            console.error('Failed to update session language:', error);
          } else {
            console.log(`Session language updated to: ${language}`);
            // Also update the session in the store
            useSessionStore.setState({
              session: { ...session, language }
            });
          }
        } catch (err) {
          console.error('Error updating session language:', err);
        }
      }
    };

    updateSessionLanguage();
  }, [language, session?.id]);

  const translateUrl = (url: string): string => {
    const parts = url.split('/').filter(Boolean);
    if (parts.length === 0) return `/${language}`;

    const urlLang = parts[0];
    if (!isValidLanguage(urlLang)) {
      return `/${language}${url}`;
    }

    // Get the canonical slug and translate it
    const slug = parts[parts.length - 1];
    const canonicalSlug = getCanonicalSlug(slug, urlLang as Language);
    const translatedSlug = getLocalizedSlug(canonicalSlug, language);

    // Replace language and slug in the URL
    parts[0] = language;
    if (parts.length > 1) {
      parts[parts.length - 1] = translatedSlug;
    }
    return '/' + parts.join('/');
  };

  const setLanguage = (newLanguage: Language) => {
    if (newLanguage === language) return;

    const pathParts = location.pathname.split('/').filter(Boolean);
    const currentLang = pathParts[0];
    
    let newPath: string;
    if (isValidLanguage(currentLang)) {
      pathParts[0] = newLanguage;
      if (pathParts.length > 1) {
        const currentSlug = pathParts[pathParts.length - 1];
        const canonicalSlug = getCanonicalSlug(currentSlug, currentLang);
        const newSlug = getLocalizedSlug(canonicalSlug, newLanguage);
        pathParts[pathParts.length - 1] = newSlug;
      }
      newPath = '/' + pathParts.join('/');
    } else {
      newPath = `/${newLanguage}${location.pathname}`;
    }
    
    setLanguageState(newLanguage);
    i18n.changeLanguage(newLanguage);
    document.documentElement.lang = newLanguage;
    navigate(newPath + location.search);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translateUrl }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}